<script lang="ts">
	/**
	 * Shared kebab trigger for every dropdown/modal that lives in the sub-tabs
	 * action slot (dashboards, all-devices, room-scoped lists, top-level lists
	 * like Rooms/Groups/Scenes/Alarms/Thermostats).
	 *
	 * Why a wrapper?
	 * Before this component each kebab was hand-rolled with subtly different
	 * defaults (icon weight, aria-label, variant, alignment, active-state).
	 * Consolidating them here guarantees:
	 *  - identical transparent `help-2` styling (matches the Figma dashboard kebab),
	 *  - consistent solid-weight ellipsis icon,
	 *  - a translated "More" aria-label,
	 *  - `align-self-center` so it lines up with tabs in the Tabs action row,
	 *  - `isActive` support so the trigger visually reflects an open dropdown.
	 *
	 * Usage: drop it into a `DropdownMenu` as `slot="trigger"` and pass an
	 * `on:click` handler. `isActive` should track the dropdown's open state.
	 * The component intentionally exposes very few knobs — keep the tabs
	 * action rail visually uniform. If you need a divergent style, prefer
	 * updating this component (so every kebab evolves together) over passing
	 * overrides here.
	 */
	import { format } from 'svelte-i18n';
	import { IconButton, type IconButtonPropsTypes } from '@Shelly/shelly-design-system';

	/** Set to the dropdown's `show` binding so the trigger reflects the open state. */
	export let isActive: boolean = false;
	/** Disable when the underlying action set is empty / unusable (e.g. empty device list). */
	export let disabled: boolean = false;
	/**
	 * Override the default "More" translation only when a more descriptive
	 * label helps screen readers (e.g. "Manage room actions").
	 */
	export let ariaLabel: string = '';
	/**
	 * IconButton variant. Locked to `help-2` (transparent) to match the
	 * dashboards' Figma treatment. Override only for exceptional cases —
	 * changing this here will drift the tabs action rail from the shared style.
	 */
	export let variant: IconButtonPropsTypes['variant'] = 'help-2';
	/**
	 * Icon override. Kept identical across every kebab (`fas fa-ellipsis-vertical`)
	 * so weight and spacing read the same everywhere.
	 */
	export let icon: string = 'fas fa-ellipsis-vertical';

	// `class` is a reserved identifier in Svelte's script scope; expose it via
	// the `className as class` idiom so callers can still add utility classes
	// (rare — the built-in `align-self-center` should suffice in most cases).
	let className: string = 'align-self-center';
	export { className as class };

	$: computedAriaLabel = ariaLabel || $format('title_more', { default: 'More' });
</script>

<IconButton
	{icon}
	{variant}
	{isActive}
	{disabled}
	aria-label={computedAriaLabel}
	class={className}
	on:click
/>
